import { GlassView } from 'expo-glass-effect';
import * as React from 'react';
import { Pressable, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

import { cn } from '../utils';

export type GlassControlPhase = 'onDark' | 'onLight';

type GlassControlProps = Omit<React.ComponentProps<typeof Pressable>, 'style'> & {
  children?: React.ReactNode;
  contentClassName?: string;
  frameHeight?: number;
  frameWidth?: number;
  label?: string;
  minWidth?: number;
  phase?: GlassControlPhase;
  style?: StyleProp<ViewStyle>;
  systemImage?: string;
  tintColor?: string;
};

function colorSchemeForPhase(phase: GlassControlPhase | undefined) {
  if (phase === 'onDark') {
    return 'dark';
  }

  if (phase === 'onLight') {
    return 'light';
  }

  return 'auto';
}

export function GlassControl({
  children,
  className,
  contentClassName,
  frameHeight,
  frameWidth,
  minWidth,
  phase,
  style,
  tintColor,
  ...props
}: Readonly<GlassControlProps>) {
  return (
    <Pressable
      className={cn(
        'relative overflow-hidden rounded-full border border-white/35 bg-white/20 active:opacity-80',
        className
      )}
      style={[{ height: frameHeight, minWidth, width: frameWidth }, style]}
      {...props}
    >
      <GlassView
        colorScheme={colorSchemeForPhase(phase)}
        glassEffectStyle="clear"
        isInteractive
        pointerEvents="none"
        style={StyleSheet.absoluteFill}
        tintColor={tintColor}
      />
      <View
        pointerEvents="none"
        style={[
          StyleSheet.absoluteFill,
          {
            backgroundColor: tintColor ? 'rgba(255,255,255,0.10)' : 'transparent',
          },
        ]}
      />
      <View className={cn('relative flex-1 items-center justify-center', contentClassName)}>
        {children}
      </View>
    </Pressable>
  );
}
